"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

// --- TYPE DEFINITIONS ---

type ProgressArcVariant = 'default' | 'secondary' | 'success' | 'warning' | 'error';

interface ProgressArcProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: ProgressArcVariant;
  value?: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
}

// --- MAIN COMPONENT ---

const ProgressArc = React.forwardRef<HTMLDivElement, ProgressArcProps>(
  ({ className, variant = "default", value = 0, max = 100, size = 120, strokeWidth = 10, ...props }, ref) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / max) * circumference;

    const colorMap: Record<ProgressArcVariant, string> = {
      default: "#38bdf8",    // sky-400
      secondary: "#a78bfa",  // violet-400
      success: "#34d399",    // emerald-400
      warning: "#fb923c",    // orange-400
      error: "#fb7185",      // rose-400
    };

    const strokeColor = colorMap[variant] || colorMap.default;

    return (
      <div
        ref={ref}
        className={cn("relative inline-flex items-center justify-center", className)}
        style={{ width: size, height: size }}
        {...props}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90 transform">
          <circle
            stroke="#e5e7eb" // gray-200
            strokeWidth={strokeWidth}
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
            style={{ transition: 'stroke-dashoffset 0.3s ease-in-out' }}
          />
        </svg>
        <div className="absolute flex items-center justify-center text-center text-gray-700">
          <p className="font-['Roboto_Condensed',_sans-serif]">
            <span className="font-medium text-[20px]">{`${String(Math.round(value)).padStart(2, '0')}/`}</span>
            <span className="font-bold text-[16px]">{String(max).padStart(2, '0')}</span>
          </p>
        </div>
      </div>
    );
  }
);

ProgressArc.displayName = "ProgressArc";

export { ProgressArc };
